import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { Currency, DonationPlan } from '../../../types/PaymentTypes';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { formatAmountAndWithDerivedLocale } from '../../../utils/helpers/currency';
import { colors, spacing } from '../../../utils/styleguide';
import AmountInput from '../forms/AmountInput';
import Typography from '../text/Typography';
import DonateAmount from './DonateAmount';

const Amounts = styled.div`
  display: flex;
  align-items: stretch;
  > label {
    margin-right: ${spacing[1]}px;
  }
  > label:last-child {
    margin-right: 0;
  }
  margin-bottom: ${spacing[2]}px;
`;

const CustomAmountInput = styled(AmountInput)`
  margin-bottom: ${spacing[2]}px;
`;

const AmountSelectorForm = styled.form`
  display: flex;
  flex-direction: column;
`;
const CustomAmountSkeleton = styled.div`
  min-height: 58px;
  margin-top: 8px;
  margin-bottom: ${spacing[2]}px;
  width: 100%;
  border: 1px solid;
  border-color: ${colors.grey};
`;

type Props = {
  amountOptions?: number[];
  customAmountLabel?: string | null;
  customAmountError?: string;
  amount?: number;
  isCustomAmount?: boolean;
  onAmountChange: (params: { amount: number; isCustomAmount: boolean; isValid: boolean }) => void;
  currency?: Currency;
  disabled?: boolean;
  className?: string;
  showSkeleton?: boolean;
  type?: DonationPlan;
  colorTheme?: 'default' | 'dark';
};

const AmountSelector = ({
  amount,
  amountOptions,
  className,
  currency,
  customAmountError,
  customAmountLabel,
  disabled,
  isCustomAmount,
  onAmountChange,
  showSkeleton,
  colorTheme,
  type = 'one time',
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<undefined | string>();
  const t = useMicrocopy();
  const minimumAmount = amountOptions?.[0];
  const minimumAmountError = `${t(
    microcopySet.PAYMENT_OPTION,
    'minimumCustomAmount',
  )} ${formatAmountAndWithDerivedLocale(minimumAmount ?? 50, currency?.code ?? 'SEK')}.`;

  function handleCustomAmountChange(event: React.ChangeEvent<HTMLInputElement>) {
    const amount = parseInt(event.target.value);
    const isValid = amount >= (minimumAmount ?? 0);
    onAmountChange({ amount, isCustomAmount: true, isValid });
    setErrorMessage(isValid ? undefined : minimumAmountError);
  }

  useEffect(() => {
    if (amount && minimumAmount) {
      const isValid = amount >= (minimumAmount ?? 0);
      setErrorMessage(isValid ? undefined : minimumAmountError);
    }
  }, [amount, minimumAmount, minimumAmountError]);

  if (showSkeleton) {
    return (
      <AmountSelectorForm className={className}>
        <Amounts>
          <DonateAmount showSkeleton={true} isSelected={false} onSelectAmount={() => {}} />
          <DonateAmount showSkeleton={true} isSelected={false} onSelectAmount={() => {}} />
          <DonateAmount showSkeleton={true} isSelected={false} onSelectAmount={() => {}} />
        </Amounts>
        {customAmountLabel && <CustomAmountSkeleton />}
      </AmountSelectorForm>
    );
  }

  return (
    <AmountSelectorForm className={className}>
      <Amounts role="radiogroup">
        {currency &&
          amountOptions?.map((amountOption, i) => (
            <DonateAmount
              key={i}
              isSelected={!isCustomAmount && amountOption === amount}
              onSelectAmount={() => {
                onAmountChange({ amount: amountOption, isCustomAmount: false, isValid: true });
              }}
              currency={currency}
              amount={amountOption}
              type={type}
              disabled={disabled}
              showSkeleton={amountOption === 0 || !currency}
              colorTheme={colorTheme}
            />
          ))}
      </Amounts>
      {customAmountLabel && currency && minimumAmount && (
        <>
          <CustomAmountInput
            amount={isCustomAmount ? amount : undefined}
            currency={currency}
            errorMessage={isCustomAmount ? errorMessage : undefined}
            isMonthlyPayment={type === 'monthly'}
            onChange={handleCustomAmountChange}
            onFocus={handleCustomAmountChange}
            placeholder={customAmountLabel}
          />

          {/** DEPRECATED? */}
          <Typography
            id="select-amount-error-message"
            useBuiltInMargins
            variant="detail"
            tag="span"
            color={colors.red}
          >
            {customAmountError}
          </Typography>
        </>
      )}
    </AmountSelectorForm>
  );
};

export default AmountSelector;
